import { type CSSProperties, useCallback } from "react";

import type { OpenAPI } from "simplydo/interfaces";
import { cx, styled } from "simplydo/react/styled";
import {
  Button,
  Icon,
  Popup,
  type SemanticCOLORS,
  type SemanticICONS,
} from "simplydo/react/ui";

import type {
  RoleEditorIcon,
  RoleIconName,
  RoleSettingsUpdateBody,
} from "../types";
import { isRoleIconName } from "../types";
import {
  ROLE_EDITOR_COLOR_OPTIONS,
  ROLE_EDITOR_ICON_OPTIONS,
  SEMANTIC_UI_COLOR_NAME_MAP,
} from "./IconChooser.constants";

const IconChooserButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 5px;
`;

const IconChooserButton = styled.div`
  cursor: pointer;
  border-radius: 5px;
  padding: 7.5px;

  &:hover:not(.is-selected) {
    background-color: #f6f6f6;
  }

  &.is-selected {
    background-color: #cfcfcf;
  }

  > i.icon {
    margin: 0;
  }
`;

const ColorChooserButton = styled.div<{ $color: SemanticCOLORS }>`
  cursor: pointer;
  border-radius: 5px;
  padding: 15px;
  background-color: ${({ $color }) => SEMANTIC_UI_COLOR_NAME_MAP[$color]};
  &:hover {
    // Grow slightly
    transform: scale(1.05);
  }
`;

type RoleEditorIconChooserProps = {
  containerStyle?: CSSProperties;
  role: OpenAPI.Schemas["Role"];
  updateRole: (data: RoleSettingsUpdateBody) => void;
};

const RoleEditorIconChooser = ({
  containerStyle = {},
  role,
  updateRole,
}: RoleEditorIconChooserProps) => {
  const { icon = { name: "", colour: "" } } = role ?? {};
  const { name: selectedIcon, colour: selectedColor } = icon;

  const setSelectedIcon = useCallback(
    (icon: SemanticICONS) => {
      if (!isRoleIconName(icon)) return;
      updateRole({ icon: { ...(role.icon ?? {}), name: icon } });
    },
    [role, updateRole],
  );

  const setSelectedColor = useCallback(
    (color: SemanticCOLORS) => {
      updateRole({ icon: { ...role.icon, colour: color } });
    },
    [role, updateRole],
  );

  const removeIcon = useCallback(() => {
    updateRole({ icon: null });
  }, [updateRole]);

  return (
    <div style={containerStyle}>
      <IconChooserButtonGroup>
        {ROLE_EDITOR_ICON_OPTIONS.map((icon) => (
          <Popup
            key={icon}
            on="click"
            position="bottom center"
            content={
              <IconChooserButtonGroup>
                {ROLE_EDITOR_COLOR_OPTIONS.map((color) => (
                  <ColorChooserButton
                    key={color}
                    $color={selectedIcon === icon ? color : "grey"}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </IconChooserButtonGroup>
            }
            trigger={
              <IconChooserButton
                className={cx(selectedIcon === icon && "is-selected")}
                onClick={() => setSelectedIcon(icon)}
              >
                <Icon
                  name={icon}
                  size="big"
                  color={icon === selectedIcon ? selectedColor : undefined}
                />
              </IconChooserButton>
            }
          />
        ))}
      </IconChooserButtonGroup>
      {selectedIcon ?
        <Button
          floated="right"
          icon="trash"
          size="mini"
          basic
          onClick={() => removeIcon()}
          content="Remove icon"
          style={{ marginTop: 10 }}
        />
      : null}
    </div>
  );
};

export default RoleEditorIconChooser;
